import type { ResumeData } from "@izicvs/schema";
import { defaultSections } from "@izicvs/schema";
import { t } from "@lingui/macro";

// Translated label for a default resume section.
const translatedLabel = (id: string): string => {
  switch (id) {
    case "basics": {
      return t`Basics`;
    }
    case "summary": {
      return t`Summary`;
    }
    case "profiles": {
      return t`Profiles`;
    }
    case "experience": {
      return t`Experience`;
    }
    case "education": {
      return t`Education`;
    }
    case "skills": {
      return t`Skills`;
    }
    case "languages": {
      return t`Languages`;
    }
    case "awards": {
      return t`Awards`;
    }
    case "certifications": {
      return t`Certifications`;
    }
    case "interests": {
      return t`Interests`;
    }
    case "projects": {
      return t`Projects`;
    }
    case "publications": {
      return t`Publications`;
    }
    case "volunteer": {
      return t`Volunteering`;
    }
    case "references": {
      return t`References`;
    }
    default: {
      return "";
    }
  }
};

// Display name for a section: the localized label while the section still
// carries its default (English) name; a user rename is shown verbatim.
export const getSectionName = (id: string, name: string): string => {
  if (id.startsWith("custom")) return name;
  // Custom sections use a bare cuid id that is absent from defaultSections, so
  // guard the lookup: an unknown id keeps its own name.
  const defaultName = (defaultSections as Record<string, { name?: string } | undefined>)[id]?.name;
  const label = translatedLabel(id);
  return label && name === defaultName ? label : name;
};

// Return a copy of the resume with each section's default title localized, so
// the rendered CV (artboard preview, public page) shows translated section
// headings the same way the editor does. User-renamed sections are untouched.
export const localizeResumeSectionNames = (data: ResumeData): ResumeData => {
  const sections = data.sections as unknown as Record<
    string,
    { id?: string; name?: string } | undefined
  >;
  const localized: Record<string, unknown> = {};
  for (const [key, section] of Object.entries(sections)) {
    localized[key] =
      section && typeof section.id === "string" && typeof section.name === "string"
        ? { ...section, name: getSectionName(section.id, section.name) }
        : section;
  }
  return { ...data, sections: localized } as ResumeData;
};
